import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as authApi from "../../api/auth";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { Logo } from "../../components/ui/Logo";

interface ResetPasswordForm {
  password: string;
  confirm: string;
}

export default function ResetPassword() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>();

  const onSubmit = handleSubmit(async ({ password }) => {
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      showToast("Password reimpostata.", "success");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile reimpostare la password. Il link potrebbe essere scaduto."), "error");
    }
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4">
        <div className="w-full max-w-sm rounded-card bg-white p-8 text-center shadow-card">
          <Logo className="mx-auto mb-6 w-44" />
          <p className="text-sm text-ink">Link non valido — manca il codice di reset.</p>
          <Link to="/forgot-password" className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">
            Richiedi un nuovo link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-8 shadow-card">
        <div className="mb-8 text-center">
          <Logo className="mx-auto w-44" />
          <p className="mt-2 text-sm text-ink/50">Scegli una nuova password</p>
        </div>

        {done ? (
          <p className="text-center text-sm text-ink">Password reimpostata — ti reindirizziamo al login…</p>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <Input
              label="Nuova password"
              type="password"
              autoComplete="new-password"
              required
              error={errors.password?.message}
              {...register("password", {
                required: "La nuova password è obbligatoria.",
                minLength: { value: 8, message: "Almeno 8 caratteri." },
              })}
            />
            <Input
              label="Conferma password"
              type="password"
              autoComplete="new-password"
              required
              error={errors.confirm?.message}
              {...register("confirm", {
                required: "Conferma la password.",
                validate: (value) => value === watch("password") || "Le due password non coincidono.",
              })}
            />
            <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
              Reimposta password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
